/**
 * Food Encoder utility module.
 *
 * Encodes and decodes food item lists into a semicolon/comma-delimited string
 * with special character escaping for use with the addJournalInput API.
 *
 * Format: {escapedName};{amount};{unitName},{escapedName};{amount};{unitName}
 */

/** Unit code: 0 = unit, 1 = ml, 2 = l */
export type UnitCode = 0 | 1 | 2;

/** Data structure for a single food item */
export interface FoodItemData {
  name: string;
  amount: number;
  unitCode: UnitCode;
}

/**
 * Escape special characters in a food name.
 * Replaces `,` with `[U002C]` and `;` with `[U003B]`.
 */
export function escapeSpecialChars(name: string): string {
  return name.replace(/,/g, '[U002C]').replace(/;/g, '[U003B]');
}

/**
 * Reverse the escape sequences in a food name.
 * Replaces `[U002C]` with `,` and `[U003B]` with `;`.
 */
export function unescapeSpecialChars(name: string): string {
  return name.replace(/\[U002C\]/g, ',').replace(/\[U003B\]/g, ';');
}

/**
 * Map a UnitCode to its string name.
 * 0 → "unit", 1 → "ml", 2 → "l"
 */
export function unitCodeToName(code: UnitCode): string {
  const map: Record<UnitCode, string> = {
    0: 'unit',
    1: 'ml',
    2: 'l',
  };
  return map[code];
}

/**
 * Map a unit name string back to its UnitCode.
 * "unit" → 0, "ml" → 1, "l" → 2
 *
 * Throws if the name is not recognized.
 */
export function unitNameToCode(name: string): UnitCode {
  const map: Record<string, UnitCode> = {
    unit: 0,
    ml: 1,
    l: 2,
  };
  const code = map[name];
  if (code === undefined) {
    throw new Error(`Unknown unit name: "${name}"`);
  }
  return code;
}

/**
 * Encode a list of food items into the API string format.
 *
 * Format: "{escapedName};{amount};{unitName},{escapedName};{amount};{unitName}"
 *
 * Example:
 *   Input: [{name: "Rice, white", amount: 200, unitCode: 1}]
 *   Output: "Rice[U002C] white;200;ml"
 */
export function encodeFoodItems(items: FoodItemData[]): string {
  return items
    .map((item) => {
      const escapedName = escapeSpecialChars(item.name);
      const unitName = unitCodeToName(item.unitCode);
      return `${escapedName};${item.amount};${unitName}`;
    })
    .join(',');
}

/**
 * Decode an encoded food string back into a list of FoodItemData.
 *
 * Parses the comma-separated items, splitting each by semicolons
 * and unescaping the food name.
 */
export function decodeFoodItems(encoded: string): FoodItemData[] {
  if (!encoded) {
    return [];
  }

  return encoded.split(',').map((segment) => {
    const parts = segment.split(';');
    const name = unescapeSpecialChars(parts[0]);
    const amount = Number(parts[1]);
    const unitCode = unitNameToCode(parts[2]);
    return { name, amount, unitCode };
  });
}
